import { useMemo, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useTourGuide, TourGuideConfig } from "./useTourGuide";
import { useAutoTourGuide } from "./useAutoTourGuide";
import { useTourGuideContext } from "@root/context/tour-guide-context";
import { updateTourCompletion } from "@store/thunks/userProfile";
import { ROUTE_NAMES } from "@root/constants";
import { TourGuideType } from "@root/constants/tour-guide";
import useMediaQ from "@root/hooks/useMediaQ";

/**
 * Tour Guide for Pledge Journalist List page.
 * Targets search bar and table. Table step mentions row click for stats and history.
 */
export const usePledgeJournalistListTourGuide = () => {
  const { t } = useTranslation("common");
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const { setActiveTourModule, setCurrentTourStep } = useTourGuideContext();
  const { sm } = useMediaQ();
  const isMobile = !sm;

  const isOnJournalistList = pathname === ROUTE_NAMES.PLEDGE_JOURNALIST_LIST;

  const config: TourGuideConfig = useMemo(
    () => ({
      moduleId: "pledgeJournalistList",
      enabled: isOnJournalistList,
      steps: [
        {
          id: "pledge-journalist-list-search",
          targetSelector: '[data-tour-item="pledge-journalist-list-search"]',
          title: t("tourGuide.pledgeJournalistList.search.title"),
          description: t("tourGuide.pledgeJournalistList.search.description"),
          position: "bottom",
          offset: { x: 0, y: 15 },
          highlightPadding: 10,
        },
        {
          id: "pledge-journalist-list-table",
          targetSelector: '[data-tour-item="pledge-journalist-list-table"]',
          title: t("tourGuide.pledgeJournalistList.table.title"),
          description: t("tourGuide.pledgeJournalistList.table.description"),
          position: "top",
          offset: { x: 0, y: -15 },
          highlightPadding: 12,
        },
      ],
    }),
    [isOnJournalistList, t],
  );

  const baseTourGuide = useTourGuide(config);

  const handleTourComplete = () => {
    dispatch(
      updateTourCompletion({
        payload: {
          tourGuides: [
            { type: TourGuideType.PledgeJournalistList, isDone: true },
          ],
        },
        onSuccess: () => {},
        onError: () => {},
      }) as any,
    );
  };

  const pledgeJournalistListTourGuide = useAutoTourGuide({
    tourGuide: baseTourGuide,
    tourGuideType: TourGuideType.PledgeJournalistList,
    isOnTourPath: isOnJournalistList && !isMobile,
    onTourComplete: handleTourComplete,
  });

  useEffect(() => {
    if (pledgeJournalistListTourGuide.isActive) {
      setActiveTourModule("pledgeJournalistList");
      setCurrentTourStep(pledgeJournalistListTourGuide.currentStep);
    } else {
      setActiveTourModule(null);
    }
  }, [
    pledgeJournalistListTourGuide.isActive,
    pledgeJournalistListTourGuide.currentStep,
    setActiveTourModule,
    setCurrentTourStep,
  ]);

  return {
    pledgeJournalistListTourGuide,
    isMobile,
  };
};
